"use client";

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { INTL_LOCALE } from "@/i18n/config";
import { addDaysIso, addMonthsIso, isoToDate, localTodayIso, monthGrid, monthStart, nightsBetween } from "@/lib/date-range";
import "./date-picker.css";

/** Formatters for the picker and its triggers, in the current language. Dates are ISO days read in UTC. */
export function useDateFormats() {
  const { locale } = useI18n();
  return useMemo(() => {
    const tag = INTL_LOCALE[locale];
    const make = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(tag, { ...o, timeZone: "UTC" });
    const short = make({ weekday: "short", day: "numeric", month: "short" });
    const shortYear = make({ weekday: "short", day: "numeric", month: "short", year: "numeric" });
    const month = make({ month: "long", year: "numeric" });
    const weekday = make({ weekday: "short" });
    const full = make({ weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return {
      /** "Fri, 16 Oct"; the year is added when it isn't the current one. */
      short: (iso: string) => (iso.slice(0, 4) === localTodayIso().slice(0, 4) ? short : shortYear).format(isoToDate(iso)),
      shortYear: (iso: string) => shortYear.format(isoToDate(iso)),
      month: (iso: string) => month.format(isoToDate(iso)),
      full: (iso: string) => full.format(isoToDate(iso)),
      // Monday-first weekday names (2024-01-01 was a Monday).
      weekdays: Array.from({ length: 7 }, (_, i) => weekday.format(isoToDate(addDaysIso("2024-01-01", i))).replace(/\.$/, "")),
    };
  }, [locale]);
}

/** The element a picker opened from `trigger` portals into: the booking-site root (so the theme applies), else the body. */
export const pickerHost = (trigger: HTMLElement | null): Element => trigger?.closest(".ps") ?? document.body;

export interface DatePickerSheetProps {
  mode: "range" | "single";
  from: string;
  to?: string;
  min?: string;
  max?: string;
  /** Range mode: the stay length is fixed (a package); only the start is picked. */
  fixedNights?: number;
  title: string;
  startLabel?: string;
  endLabel?: string;
  /** Show a button that empties the value. */
  clearable?: boolean;
  /** Where the sheet is portalled: the booking-site root (for its theme) or the body. See `pickerHost`. */
  host: Element;
  onApply: (from: string, to: string) => void;
  onClose: () => void;
}

/**
 * The calendar sheet shared by the booking site and the staff app: a bottom sheet on phones, a dialog on larger screens
 * (two months side by side for a range). Range: tap the start day, then the end day, then apply. Single: tap a day.
 * Colours come from the booking site's theme tokens when inside it, otherwise from the app's.
 */
export function DatePickerSheet({ mode, from: initialFrom, to: initialTo = "", min, max, fixedNights, title, startLabel, endLabel, clearable, host, onApply, onClose }: DatePickerSheetProps) {
  const { t } = useI18n();
  const fmt = useDateFormats();
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [month, setMonth] = useState(monthStart(initialFrom || (min && min > localTodayIso() ? min : localTodayIso())));
  const [wide] = useState(() => mode === "range" && typeof window !== "undefined" && window.matchMedia("(min-width: 721px)").matches);
  const today = localTodayIso();
  const range = mode === "range";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const pick = (day: string) => {
    if (!range) {
      onApply(day, "");
      return;
    }
    if (fixedNights) {
      setFrom(day);
      setTo(addDaysIso(day, fixedNights));
    } else if (!from || to || day <= from) {
      // First tap (or a new start): the start day; the next tap after it is the end.
      setFrom(day);
      setTo("");
    } else {
      setTo(day);
    }
  };

  const months = wide ? [month, addMonthsIso(month, 1)] : [month];
  const canGoBack = !min || month > monthStart(min);
  const canGoForward = !max || addMonthsIso(month, months.length) <= monthStart(max);
  const nights = nightsBetween(from, to);
  const waitingForEnd = range && !fixedNights && Boolean(from) && !to;
  const todayAllowed = (!min || today >= min) && (!max || today <= max);

  return createPortal(
    <div className="dp-back" onClick={onClose}>
      <div className={`dp${range ? " dp-range" : " dp-single"}${wide ? " is-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="dp-head">
          <strong>{title}</strong>
          <button type="button" className="dp-x" onClick={onClose} aria-label={t("common.datePicker.close")}>{"✕"}</button>
        </div>

        {range && (
          <>
            <div className="dp-summary">
              <div className={waitingForEnd ? "" : "is-next"}>
                <small>{startLabel}</small>
                <strong>{from ? fmt.short(from) : "—"}</strong>
              </div>
              <div className={waitingForEnd ? "is-next" : ""}>
                <small>{endLabel}</small>
                <strong>{to ? fmt.short(to) : "—"}</strong>
              </div>
            </div>
            <p className="dp-hint">
              {fixedNights
                ? t("common.datePicker.hintFixed", { nights: t("common.datePicker.nights", { count: fixedNights }) })
                : waitingForEnd
                  ? t("common.datePicker.hintEnd", { label: (endLabel || "").toLowerCase() })
                  : t("common.datePicker.hintStart", { label: (startLabel || "").toLowerCase() })}
            </p>
          </>
        )}

        <div className="dp-nav">
          <button type="button" onClick={() => setMonth(addMonthsIso(month, -1))} disabled={!canGoBack} aria-label={t("common.datePicker.prevMonth")}>{"‹"}</button>
          <button type="button" onClick={() => setMonth(addMonthsIso(month, 1))} disabled={!canGoForward} aria-label={t("common.datePicker.nextMonth")}>{"›"}</button>
        </div>
        <div className="dp-months">
          {months.map((m) => (
            <div key={m}>
              <div className="dp-title">{fmt.month(m)}</div>
              <div className="dp-grid" role="grid">
                {fmt.weekdays.map((w, i) => <span key={`w${i}`} className="dp-wd">{w}</span>)}
                {monthGrid(m).map((day, i) => {
                  if (!day) return <span key={`e${i}`} />;
                  const disabled = Boolean((min && day < min) || (max && day > max));
                  const isStart = day === from;
                  const isEnd = range && day === to;
                  const inRange = range && from && to && day > from && day < to;
                  const cls = ["dp-day", isStart && "is-start", isEnd && "is-end", inRange && "is-in", day === today && "is-today", range && from && to && "has-range"]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <button key={day} type="button" className={cls} disabled={disabled} onClick={() => pick(day)} aria-pressed={isStart || isEnd} aria-label={fmt.full(day)}>
                      {Number(day.slice(8))}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="dp-foot">
          <div className="dp-foot-left">
            {clearable && (
              <button
                type="button"
                className="dp-link"
                onClick={() => {
                  if (!range) return onApply("", "");
                  setFrom("");
                  setTo("");
                }}
              >
                {t("common.datePicker.clear")}
              </button>
            )}
            {!range && todayAllowed && (
              <button type="button" className="dp-link" onClick={() => onApply(today, "")}>{t("common.datePicker.today")}</button>
            )}
          </div>
          {range && (
            <button
              type="button"
              className="dp-apply"
              disabled={!(from && to) && !(clearable && !from && !to)}
              onClick={() => onApply(from, to)}
            >
              {nights > 0 ? t("common.datePicker.applyNights", { nights: t("common.datePicker.nights", { count: nights }) }) : t("common.datePicker.apply")}
            </button>
          )}
        </div>
      </div>
    </div>,
    host
  );
}
